import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jobsAPI } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save } from 'lucide-react';

import { JOB_CATEGORIES as CATEGORIES } from '@/constants/jobCategories';
import { JobWorkFields } from '@/components/JobWorkFields';
const EMPLOYMENT_TYPES = ['full-time', 'part-time', 'contract', 'internship', 'temporary'];

export default function EditJob() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await jobsAPI.getById(id);
        setFormData({
          title_pt: data.title_pt || '',
          description_pt: data.description_pt || '',
          requirements_pt: data.requirements_pt || '',
          location: data.location || '',
          category: data.category || '',
          employment_type: data.employment_type || 'full-time',
          salary_range_min: data.salary_range_min ?? '',
          salary_range_max: data.salary_range_max ?? '',
          salary_hidden: !!data.salary_hidden,
          currency: data.currency || 'CVE',
          vacancies_count: data.vacancies_count ?? 1,
          is_featured: !!data.is_featured,
          work_mode: data.work_mode || 'presencial',
          contract_type: data.contract_type || '',
          languages: data.languages || [],
        });
      } catch (e) {
        setError(e?.response?.data?.detail || t('editJob.notFound'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title_pt || !formData.description_pt || !formData.requirements_pt
        || !formData.location || !formData.category) {
      setError(t('editJob.errorRequiredFields'));
      return;
    }
    const salMin = formData.salary_range_min !== '' ? Number(formData.salary_range_min) : null;
    const salMax = formData.salary_range_max !== '' ? Number(formData.salary_range_max) : null;
    if (salMin !== null && salMax !== null && salMin > salMax) {
      setError(t('editJob.errorSalaryMinMax'));
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        ...formData,
        salary_range_min: formData.salary_range_min === '' ? null : Number(formData.salary_range_min),
        salary_range_max: formData.salary_range_max === '' ? null : Number(formData.salary_range_max),
        salary_hidden: !!formData.salary_hidden,
        vacancies_count: Math.max(1, Math.min(50, parseInt(formData.vacancies_count || 1, 10))),
      };
      await jobsAPI.update(id, payload);
      setMessage(t('editJob.updated'));
      setTimeout(() => navigate('/my-jobs'), 900);
    } catch (e) {
      setError(e?.response?.data?.detail || t('editJob.errorUpdate'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="edit-job-loading">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-3xl mx-auto px-4">
          <Button variant="ghost" onClick={() => navigate('/my-jobs')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('editJob.back')}
          </Button>
          <Alert variant="destructive" data-testid="edit-job-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="edit-job-page">
      <div className="max-w-3xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/my-jobs')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />{t('editJob.backToMyJobs')}
        </Button>

        <Card className="p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('editJob.title')}</h1>
          <p className="text-gray-600 mb-6">{t('editJob.subtitle')}</p>

          {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}
          {message && (
            <Alert className="mb-4 border-green-300 bg-green-50 text-green-900" data-testid="edit-job-success">
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSave} className="space-y-5" data-testid="edit-job-form">
            <div>
              <Label htmlFor="title_pt">{t('editJob.fieldTitle')} *</Label>
              <Input
                id="title_pt" value={formData.title_pt} required
                onChange={(e) => setFormData({ ...formData, title_pt: e.target.value })}
                data-testid="edit-job-title-input"
              />
            </div>

            <div>
              <Label htmlFor="description_pt">{t('editJob.fieldDescription')} *</Label>
              <Textarea
                id="description_pt" value={formData.description_pt} rows={6} required
                onChange={(e) => setFormData({ ...formData, description_pt: e.target.value })}
                data-testid="edit-job-description-input"
              />
            </div>

            <div>
              <Label htmlFor="requirements_pt">{t('editJob.fieldRequirements')} *</Label>
              <Textarea
                id="requirements_pt" value={formData.requirements_pt} rows={4} required
                onChange={(e) => setFormData({ ...formData, requirements_pt: e.target.value })}
                data-testid="edit-job-requirements-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">{t('editJob.fieldLocation')} *</Label>
                <Input
                  id="location" value={formData.location} required
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Praia, Santiago"
                  data-testid="edit-job-location-input"
                />
              </div>
              <div>
                <Label htmlFor="category">{t('editJob.fieldCategory')} *</Label>
                <select
                  id="category" value={formData.category} required
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm"
                  data-testid="edit-job-category-select"
                >
                  <option value="">{t('editJob.selectPlaceholder')}</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{t(`jobSearch.categories.${c}`)}</option>)}
                </select>
              </div>
            </div>

            <JobWorkFields formData={formData} setFormData={setFormData} testPrefix="edit-job" />

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="employment_type">{t('editJob.fieldEmploymentType')}</Label>
                <select
                  id="employment_type" value={formData.employment_type}
                  onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md bg-white text-sm"
                  data-testid="edit-job-employment-type-select"
                >
                  {EMPLOYMENT_TYPES.map(v => <option key={v} value={v}>{t(`jobSearch.employmentTypes.${v}`)}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="salary_range_min">{t('editJob.fieldSalaryMin')}</Label>
                <Input
                  id="salary_range_min" type="number" min="0"
                  value={formData.salary_range_min}
                  onChange={(e) => setFormData({ ...formData, salary_range_min: e.target.value })}
                  placeholder="Ex: 40000"
                  data-testid="edit-job-salary-min-input"
                />
              </div>
              <div>
                <Label htmlFor="salary_range_max">{t('editJob.fieldSalaryMax')}</Label>
                <Input
                  id="salary_range_max" type="number" min="0"
                  value={formData.salary_range_max}
                  onChange={(e) => setFormData({ ...formData, salary_range_max: e.target.value })}
                  placeholder="Ex: 80000"
                  data-testid="edit-job-salary-max-input"
                />
              </div>
            </div>
            {/* Toggle ocultar salário */}
            <label className="flex items-start gap-3 cursor-pointer select-none mt-2" data-testid="edit-job-hide-salary-toggle">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={!!formData.salary_hidden}
                  onChange={(e) => setFormData({ ...formData, salary_hidden: e.target.checked })}
                />
                <div className={`w-10 h-5 rounded-full transition-colors ${formData.salary_hidden ? 'bg-blue-600' : 'bg-gray-300'}`} />
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.salary_hidden ? 'translate-x-5' : ''}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">{t('editJob.hideSalary')}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t('editJob.hideSalaryHint')}</p>
              </div>
            </label>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vacancies_count">
                  {t('editJob.fieldVacancies')}
                  <span className="ml-1 text-xs text-gray-500 font-normal">(1–50)</span>
                </Label>
                <Input
                  id="vacancies_count" type="number" min="1" max="50"
                  value={formData.vacancies_count}
                  onChange={(e) => setFormData({ ...formData, vacancies_count: e.target.value })}
                  data-testid="edit-job-vacancies-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => navigate('/my-jobs')}>
                {t('editJob.cancel')}
              </Button>
              <Button
                type="submit" disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="edit-job-save-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('editJob.saving') : t('editJob.saveChanges')}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
